import { isNumber, round } from 'lodash';
import { AGGREGATORS, CHART_AGGREGATORS, CHART_TYPE, DS_ID } from 'src/constants/chart';
import { formatIntervalInDuration } from 'src/utils/DateUtils';

export default function dsTargets() {
  return {
    id: DS_ID.TARGETS,
    label: this.$t('insights.chartConfigurator.sourcesTargetsLabel'),
    hasDateRange: true,
    dateRangeDimensions: [
      {
        value: null,
        label: this.$t('insights.chartConfigurator.dateRangeDimensions.none'),
      },
      {
        value: 'masteryDate',
        label: this.$t('insights.chartConfigurator.dateRangeDimensions.masteryDate'),
      },
      {
        value: 'introductionDate',
        label: this.$t('insights.chartConfigurator.dateRangeDimensions.introductionDate'),
      },
    ],

    // True when program type does not have to be loaded separately for filtering
    dataContainsProgramType: true,
    dataContainsTargetStatus: true,

    availableFilters: ['programs', 'programTags', 'programTypes', 'targetStatuses'],

    groupByOptions: [
      {
        label: this.$t('insights.chartConfigurator.groupByNoGroupLabel'),
        value: null,
        seriesLabelFrom: null,
      },
      {
        label: this.$t('insights.chartConfigurator.groupByProgramLabel'),
        value: 'programId',
        seriesLabelFrom: 'programName',
        seriesOrderBy: 'programName',
      },
      {
        label: this.$t('insights.chartConfigurator.groupByTargetLabel'),
        value: 'targetId',
        seriesLabelFrom: 'targetLabel',
        seriesOrderBy: 'targetOrder',
      },
    ],

    xAxes: [
      {
        id: 'xaMasteredDateDaily',
        label: this.$t('insights.chartConfigurator.xAxisMasteredDateDailyLabel'),
        chartType: CHART_TYPE.LINE,
        xAxisType: 'date',
        xAxisAggregateBy: null,
        xAxisDataProperty: 'targetMasteryDate',
        xAxisCategoryLabel: null,
        xAxisTimeUnit: 'day',
        xAxisOrderBy: 'targetMasteryDate',
        xAxisSplitBy: null,
      },
      {
        id: 'xaMasteredDateWeekly',
        label: this.$t('insights.chartConfigurator.xAxisMasteredDateWeeklyLabel'),
        chartType: CHART_TYPE.LINE,
        xAxisType: 'date',
        xAxisAggregateBy: null,
        xAxisDataProperty: 'targetMasteryDate',
        xAxisCategoryLabel: null,
        xAxisTimeUnit: 'week',
        xAxisOrderBy: 'targetMasteryDate',
        xAxisSplitBy: null,
      },
      {
        id: 'xaMasteredDateMonthly',
        label: this.$t('insights.chartConfigurator.xAxisMasteredDateMonthlyLabel'),
        chartType: CHART_TYPE.COLUMN,
        xAxisType: 'date',
        xAxisAggregateBy: null,
        xAxisDataProperty: 'targetMasteryDate',
        xAxisCategoryLabel: null,
        xAxisTimeUnit: 'month',
        xAxisOrderBy: 'targetMasteryDate',
        xAxisSplitBy: null,
      },
      {
        id: 'xaIntroductionDateDaily',
        label: this.$t('insights.chartConfigurator.xAxisIntroductionDateDailyLabel'),
        chartType: CHART_TYPE.LINE,
        xAxisType: 'date',
        xAxisAggregateBy: null,
        xAxisDataProperty: 'targetIntroductionDate',
        xAxisCategoryLabel: null,
        xAxisTimeUnit: 'day',
        xAxisOrderBy: 'targetIntroductionDate',
        xAxisSplitBy: null,
      },
      {
        id: 'xaIntroductionDateWeekly',
        label: this.$t('insights.chartConfigurator.xAxisIntroductionDateWeeklyLabel'),
        chartType: CHART_TYPE.LINE,
        xAxisType: 'date',
        xAxisAggregateBy: null,
        xAxisDataProperty: 'targetIntroductionDate',
        xAxisCategoryLabel: null,
        xAxisTimeUnit: 'week',
        xAxisOrderBy: 'targetIntroductionDate',
        xAxisSplitBy: null,
      },
      {
        id: 'xaIntroductionDateMonthly',
        label: this.$t('insights.chartConfigurator.xAxisIntroductionDateMonthlyLabel'),
        chartType: CHART_TYPE.COLUMN,
        xAxisType: 'date',
        xAxisAggregateBy: null,
        xAxisDataProperty: 'targetIntroductionDate',
        xAxisCategoryLabel: null,
        xAxisTimeUnit: 'month',
        xAxisOrderBy: 'targetIntroductionDate',
        xAxisSplitBy: null,
      },
      {
        id: 'xaProgram',
        label: this.$t('insights.chartConfigurator.xAxisProgramLabel'),
        chartType: CHART_TYPE.COLUMN,
        xAxisType: 'category',
        xAxisAggregateBy: 'programId',
        xAxisDataProperty: 'programId',
        xAxisCategoryLabel: 'programName',
        xAxisTimeUnit: null,
        xAxisOrderBy: 'programName',
        xAxisSplitBy: null,
      },
      {
        id: 'xaTarget',
        label: this.$t('insights.chartConfigurator.xAxisTargetLabel'),
        chartType: CHART_TYPE.COLUMN,
        xAxisType: 'category',
        xAxisAggregateBy: 'targetId',
        xAxisDataProperty: 'targetId',
        xAxisCategoryLabel: 'targetLabel',
        xAxisTimeUnit: null,
        xAxisOrderBy: 'targetLabel',
        xAxisSplitBy: null,
      },
    ],

    seriesPresets: [
      {
        id: 'spCumulativeCount',

        label: this.$t('insights.chartConfigurator.seriesCumulative'),
        dataProperty: 'ACC_COUNT',
        aggregator: AGGREGATORS.CLOSE,
        chartAggregator: CHART_AGGREGATORS.HIGH,
        axis: 'number',
        axisCategory: null,
        axisCategoryLabel: null,
        hideColumnInTable: true,
        disableChartTypes: [CHART_TYPE.SCATTERPLOT],
      },
      {
        id: 'spCount',
        label: this.$t('insights.chartConfigurator.seriesTargets'),
        dataProperty: 'COUNT',
        countDataProperty: 'targetId',
        aggregator: AGGREGATORS.SUM,
        chartAggregator: CHART_AGGREGATORS.SUM,
        axis: 'number',
        axisCategory: null,
        axisCategoryLabel: null,
        hideColumnInTable: false,
        disableChartTypes: [CHART_TYPE.SCATTERPLOT],
      },
      {
        id: 'spLearningRate',
        label: this.$t('insights.chartConfigurator.seriesLearningRate'),
        dataProperty: 'targetLearningRate',
        aggregator: AGGREGATORS.AVERAGE,
        chartAggregator: CHART_AGGREGATORS.AVERAGE,
        axis: 'number',
        axisCategory: null,
        axisCategoryLabel: null,
        hideColumnInTable: false,
        disableChartTypes: [CHART_TYPE.SCATTERPLOT],
      },
      {
        id: 'spLearningRateSessions',
        label: this.$t('insights.chartConfigurator.seriesLearningRateSessions'),
        dataProperty: 'targetSessionsToAcquire',
        aggregator: AGGREGATORS.AVERAGE,
        chartAggregator: CHART_AGGREGATORS.AVERAGE,
        axis: 'number',
        axisCategory: null,
        axisCategoryLabel: null,
        hideColumnInTable: false,
        disableChartTypes: [CHART_TYPE.SCATTERPLOT],
      },
    ],

    tableColumns: [
      {
        name: 'programName',
        field: 'programName',
        label: this.$t('insights.chartConfigurator.columnsProgram'),
        align: 'left',
        classes: 'insights-truncate-label',
        sortable: true,
        visibility: [null, 'programId', 'targetId'],
        visibilityExport: [null, 'programId', 'targetId'],
      },
      {
        name: 'targetLabel',
        field: 'targetLabel',
        label: this.$t('insights.chartConfigurator.columnsTarget'),
        align: 'left',
        classes: 'insights-truncate-label',
        sortable: true,
        visibility: [null, 'targetId'],
        visibilityExport: [null, 'targetId'],
      },
      {
        name: 'targetStatus',
        field: 'targetStatus',
        label: this.$t('insights.chartConfigurator.columnsStatus'),
        align: 'center',
        visibility: [null, 'targetId'],
        visibilityExport: [null, 'targetId'],
      },
      {
        name: 'targetLearningRate',
        field: 'targetLearningRate',
        format: (val) => (isNumber(val) ? formatIntervalInDuration(new Date(), new Date(), val) : val),
        label: this.$t('insights.chartConfigurator.columnsTimeToLearn'),
        align: 'left',
        sortable: true,
        visibility: [null, 'targetId'],
        visibilityExport: [null, 'targetId'],
      },
      {
        name: 'targetLearningRateSessions',
        field: 'targetSessionsToAcquire',
        format: (val) => (isNumber(val) ? round(val, 0) : ''),
        label: this.$t('insights.chartConfigurator.columnsSessionsToLearn'),
        align: 'left',
        sortable: true,
        visibility: [null, 'targetId'],
        visibilityExport: [null, 'targetId'],
      },
      {
        name: 'targetIntroductionDate',
        field: 'targetIntroductionDate',
        format: (val) => (val ? this.getLocaleDateString(val) : ''),
        label: this.$t('insights.chartConfigurator.columnsIntroduced'),
        align: 'center',
        sortable: true,
        visibility: [null, 'targetId'],
        visibilityExport: [null, 'targetId'],
      },
      {
        name: 'targetMasteryDate',
        field: 'targetMasteryDate',
        format: (val) => (val ? this.getLocaleDateString(val) : ''),
        label: this.$t('insights.chartConfigurator.columnsMastered'),
        align: 'center',
        sortable: true,
        visibility: [null, 'targetId'],
        visibilityExport: [null, 'targetId'],
      },
    ],
  };
}
